import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Shield, Download, Filter, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { amlAPI } from '../lib/amlAPI';
import { formatAUDate } from '../lib/dateUtils';
import { formatRelativeTime } from '../lib/fileUtils';

export default function AMLAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEventType, setFilterEventType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadLogs();
  }, [filterEventType, startDate, endDate]);

  const loadLogs = async () => {
    try {
      const response = await amlAPI.listAuditLogs({
        event_type: filterEventType !== 'all' ? filterEventType : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        limit: 100
      });
      setLogs(response.data || []);
    } catch (error) {
      toast.error('Failed to load audit logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'Event Type', 'Client', 'Actor', 'IP Address', 'Retention Until'];
    const csvData = logs.map(log => [
      new Date(log.timestamp).toLocaleString('en-AU'),
      log.event_type,
      log.client_name || 'N/A',
      log.actor_name,
      log.ip_address || 'N/A',
      formatAUDate(log.retention_until)
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `aml-audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Audit logs exported to CSV');
  };

  const eventTypeColors = {
    kyc_completed: 'bg-green-100 text-green-800',
    risk_assessed: 'bg-blue-100 text-blue-800',
    risk_assessment_approved: 'bg-accent text-accent-foreground',
    risk_assessment_updated: 'bg-amber-100 text-amber-800',
    kyc_record_created: 'bg-purple-100 text-purple-800',
    kyc_record_updated: 'bg-indigo-100 text-indigo-800',
    review_scheduled: 'bg-cyan-100 text-cyan-800'
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="audit-logs-title">
            AML Audit Logs
          </h1>
          <p className="text-muted-foreground mt-1">7-year retention audit trail for all AML/CTF activities</p>
        </div>
        <Button onClick={exportToCSV} variant="outline" data-testid="export-csv-button">
          <Download className="w-4 h-4 mr-2" />
          Export to CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground/90">Event Type</label>
              <Select value={filterEventType} onValueChange={setFilterEventType}>
                <SelectTrigger data-testid="filter-event-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="kyc_completed">KYC Completed</SelectItem>
                  <SelectItem value="risk_assessed">Risk Assessed</SelectItem>
                  <SelectItem value="risk_assessment_approved">Assessment Approved</SelectItem>
                  <SelectItem value="review_scheduled">Review Scheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground/90">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground/90">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => {
                  setFilterEventType('all');
                  setStartDate('');
                  setEndDate('');
                }}
                variant="outline"
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardContent className="pt-6">
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No audit logs found for the selected filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Timestamp</TableHead>
                    <TableHead className="font-semibold">Event Type</TableHead>
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold">Actor</TableHead>
                    <TableHead className="font-semibold">IP Address</TableHead>
                    <TableHead className="font-semibold">Retention Until</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-muted/50" data-testid={`audit-log-${log.id}`}>
                      <TableCell className="font-mono text-xs">
                        <div>{formatAUDate(log.timestamp)}</div>
                        <div className="text-muted-foreground">{formatRelativeTime(log.timestamp)}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={eventTypeColors[log.event_type] || 'bg-muted text-foreground'}>
                          {log.event_type.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>{log.client_name || <span className="text-muted-foreground/70">N/A</span>}</TableCell>
                      <TableCell>{log.actor_name}</TableCell>
                      <TableCell className="font-mono text-xs">{log.ip_address || <span className="text-muted-foreground/70">N/A</span>}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatAUDate(log.retention_until)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">AUSTRAC Compliance</h4>
                <p className="text-xs text-blue-800">
                  All AML/CTF audit logs are retained for 7 years from the date of creation in accordance with AUSTRAC requirements.
                  Total records: {logs.length}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}